import React from 'react';
import { Link } from 'react-router-dom';

import SubtleBadge from 'components/common/SubtleBadge';

import paths from 'routes/paths';
import {
  formatStatus,
  formatInventoryHistoryMethod,
} from 'utils/valueFormatters';
import {
  getInventoryBadgeColor,
  getInventoryHistoryMethodBadgeColor,
} from 'constants/inventory';
import { TIME_OPTION } from 'constants/time';

const CONTEXT_ROUTE_MAP = {
  new_order: 'orders',
  order_cancel: 'orders',
  order_reject_not_found: 'orders',
  order_reject_damaged: 'orders',
  order_transfer: 'orders',
  manifest_close: 'manifests',
  return_manifest_close: 'returns',
  gate_pass_item_create: 'gatePasses',
  gate_pass_submit: 'gatePasses',
  gate_pass_item_delete: 'gatePasses',
  put_away: 'putAway',
  stocktaking: 'stocktakings',
};

const getContextDetailPath = (context, targetObjectId, tenant, warehouse) => {
  const routeKey = CONTEXT_ROUTE_MAP[context];
  if (!routeKey) return null;
  return paths[routeKey].details(tenant, warehouse, targetObjectId);
};

export const INVENTORY_LIST_COLUMNS = [
  {
    accessorKey: 'product_sku',
    header: 'SKU Code',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { product } = original;

      return <span>{product.sku}</span>;
    },
  },
  {
    accessorKey: 'product_name',
    header: 'Item Name',
    enableSorting: true,
    meta: {
      valueType: 'string',
      truncate: true,
      minWidth: '300px',
      maxWidth: '300px',
    },
    cell: ({ row: { original } }) => {
      const { product } = original;

      return <span className="d-block text-truncate">{product.name}</span>;
    },
  },
  {
    accessorKey: 'brand_name',
    header: 'Brand',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { product } = original;

      return <span>{product?.brand?.name || '-'}</span>;
    },
  },
  {
    accessorKey: 'inventory_type',
    header: 'Category',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { inventory_type } = original;

      return (
        <SubtleBadge pill bg={getInventoryBadgeColor(inventory_type)}>
          {formatStatus(inventory_type)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'shelf_code',
    header: 'Shelf',
    enableSorting: true,
    meta: {
      valueType: 'string',
      minWidth: '90px',
    },
    cell: ({ row: { original } }) => {
      const { shelf } = original;

      return <span>{shelf.code}</span>;
    },
  },
  {
    accessorKey: 'expire_date',
    header: 'Expiry Date',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { expire_date } = original;

      return <span>{expire_date || '-'}</span>;
    },
  },
  {
    accessorKey: 'batch_code',
    header: 'Batch Code',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { batch_code } = original;

      return <span>{batch_code || '-'}</span>;
    },
  },
  {
    accessorKey: 'quantity',
    header: 'Total Quantity',
    enableSorting: true,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { quantity } = original;

      return <span>{quantity}</span>;
    },
  },
  {
    accessorKey: 'quantity_available',
    header: 'Available Quantity',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { quantity, quantity_allocated } = original;

      return <span>{quantity - quantity_allocated}</span>;
    },
  },
  {
    accessorKey: 'quantity_allocated',
    header: 'Allocated Quantity',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { quantity_allocated } = original;

      return <span>{quantity_allocated}</span>;
    },
  },
  {
    accessorKey: 'modified',
    header: 'Updated',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { modified } = original;

      return (
        <span>{new Date(modified).toLocaleString('en-IN', TIME_OPTION)}</span>
      );
    },
  },
];

export const getInventoryHistoryListColumns = (tenant, warehouse) => [
  {
    accessorKey: 'product_sku',
    header: 'SKU Code',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { product_batch } = original;

      return <span>{product_batch?.product?.sku}</span>;
    },
  },
  {
    accessorKey: 'product_name',
    header: 'Product Name',
    enableSorting: true,
    meta: {
      valueType: 'string',
      truncate: true,
      minWidth: '300px',
      maxWidth: '300px',
    },
    cell: ({ row: { original } }) => {
      const { product_batch } = original;

      return (
        <span
          className="d-block text-truncate"
          title={product_batch?.product?.name}
        >
          {product_batch?.product?.name}
        </span>
      );
    },
  },
  {
    accessorKey: 'shelf_code',
    header: 'Shelf Code',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { product_batch } = original;

      return <span>{product_batch?.shelf?.code}</span>;
    },
  },
  {
    accessorKey: 'product_batch_expire_date',
    header: 'Expiry Date',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { product_batch } = original;

      return <span>{product_batch?.expire_date || '-'}</span>;
    },
  },
  {
    accessorKey: 'product_batch_batch_code',
    header: 'Batch Code',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { product_batch } = original;

      return <span>{product_batch?.batch_code || '-'}</span>;
    },
  },
  {
    accessorKey: 'inventory_history_method',
    header: 'Type',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { inventory_history_method } = original;

      return (
        <SubtleBadge
          pill
          bg={getInventoryHistoryMethodBadgeColor(inventory_history_method)}
        >
          {formatInventoryHistoryMethod(inventory_history_method)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'old_quantity',
    header: 'Old Quantity',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { quantity, added_quantity } = original;

      return <span>{quantity - added_quantity}</span>;
    },
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      const { quantity } = original;

      return <span>{quantity}</span>;
    },
  },
  {
    accessorKey: 'inventory_type',
    header: 'Category',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { product_batch } = original;

      return (
        <SubtleBadge
          pill
          bg={getInventoryBadgeColor(product_batch.inventory_type)}
        >
          {formatStatus(product_batch.inventory_type)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'context',
    header: 'Context',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { context } = original;

      return <span>{context ? formatStatus(context) : '-'}</span>;
    },
  },
  {
    accessorKey: 'comments',
    header: 'Comments',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { comments, context, target_object_id } = original;

      if (!comments) return <span>-</span>;

      const detailPath = getContextDetailPath(
        context,
        target_object_id,
        tenant,
        warehouse,
      );

      if (!detailPath) return <span>{comments}</span>;

      return (
        <Link to={detailPath}>
          <strong>{comments}</strong>
        </Link>
      );
    },
  },
  {
    accessorKey: 'user_email',
    header: 'Adjusted By',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { user } = original;

      return <span>{user?.email || '-'}</span>;
    },
  },
  {
    accessorKey: 'created',
    header: 'Created',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { created } = original;

      return (
        <span>{new Date(created).toLocaleString('en-IN', TIME_OPTION)}</span>
      );
    },
  },
];
